import { useRef, useState } from 'react';
import { Alert, Icon, V2Badge, V2Button, V2Card, V2CardHeader } from '@sakhtar/design-system/react';
import type { GuidedImportPack } from '../../lib/dataImport/guidedImportTemplate.js';
import './importPackCard.css';

export function importPackModule(id: string) {
  return id === 'suppliers' ? 'procurement' : id === 'sales' ? 'crm' : id;
}

/** Uses the same module scope, patterned header and primitives as module hubs. */
export function ImportPackCard({
  pack,
  file,
  busy,
  onChoose,
  onTemplate,
}: {
  readonly pack: GuidedImportPack;
  readonly file?: File | undefined;
  readonly busy: boolean;
  readonly onChoose: (file: File | null) => void;
  readonly onTemplate: () => void;
}) {
  const input = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [dropError, setDropError] = useState(false);
  const disabled = busy || !pack.permitted;
  return (
    <section
      className="import-pack"
      data-module={importPackModule(pack.id)}
      aria-label={pack.label}
    >
      <V2Card className={`import-pack__card${file ? ' import-pack__card--selected' : ''}`}>
        <V2CardHeader
          title={pack.label}
          icon={
            pack.id === 'crm'
              ? 'users'
              : pack.id === 'finance'
                ? 'finance'
                : pack.id === 'inventory'
                  ? 'inventory'
                  : pack.id === 'sales'
                    ? 'sales'
                    : 'building'
          }
          subtitle={`${pack.sheets.length.toLocaleString('fa-IR')} شیت · قالب Excel`}
          actions={
            <V2Badge tone={!pack.permitted ? 'neutral' : file ? 'success' : 'neutral'}>
              {!pack.permitted ? 'بدون دسترسی' : file ? 'فایل انتخاب شد' : 'اختیاری'}
            </V2Badge>
          }
        />
        <div className="import-pack__body">
          <p className="import-pack__description">{pack.description}</p>
          <div className="import-pack__sheets" aria-label="محتوای قالب">
            {pack.sheets.map((sheet) => (
              <span key={sheet.id}>{sheet.name}</span>
            ))}
          </div>
          <div className="import-pack__template">
            <span>
              <strong>۱. قالب این بخش را تکمیل کنید</strong>
              <small>عنوان ستون‌ها و کدهای ارتباط را حفظ کنید.</small>
            </span>
            <V2Button
              variant="secondary"
              size="sm"
              icon="arrowDown"
              disabled={disabled}
              onClick={onTemplate}
            >
              دریافت نمونهٔ {pack.label}
            </V2Button>
          </div>
          <div
            className={`import-pack__upload${dragging ? ' is-dragging' : ''}${file ? ' has-file' : ''}`}
            onDragOver={(event) => {
              event.preventDefault();
              if (!disabled) setDragging(true);
            }}
            onDragLeave={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null))
                setDragging(false);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              if (!disabled) {
                if (event.dataTransfer.files.length !== 1) {
                  setDropError(true);
                  return;
                }
                setDropError(false);
                onChoose(event.dataTransfer.files[0] ?? null);
              }
            }}
          >
            <span className="import-pack__upload-icon" aria-hidden="true">
              <Icon name={file ? 'check' : 'upload'} size="md" />
            </span>
            <strong>{file ? 'فایل آمادهٔ بررسی است' : '۲. فایل تکمیل‌شده را بارگذاری کنید'}</strong>
            {file ? (
              <span className="import-pack__file" dir="auto">
                {file.name}
              </span>
            ) : (
              <small>فایل را اینجا رها کنید یا از دستگاه انتخاب کنید.</small>
            )}
            <small>
              {file
                ? `${Math.max(1, Math.ceil(file.size / 1024)).toLocaleString('fa-IR')} کیلوبایت · هنوز ثبت نشده`
                : 'فقط .xlsx · حداکثر ۵ مگابایت'}
            </small>
            <div className="import-pack__file-actions">
              <V2Button
                variant={file ? 'secondary' : 'primary'}
                size="sm"
                icon="upload"
                disabled={disabled}
                onClick={() => input.current?.click()}
              >
                {file ? `تغییر فایل ${pack.label}` : `انتخاب فایل ${pack.label}`}
              </V2Button>
              {file ? (
                <V2Button
                  variant="ghost"
                  size="sm"
                  disabled={disabled}
                  onClick={() => onChoose(null)}
                >
                  حذف فایل {pack.label}
                </V2Button>
              ) : null}
            </div>
            <input
              ref={input}
              hidden
              aria-label={`فایل ${pack.label}`}
              type="file"
              accept=".xlsx"
              disabled={disabled}
              onChange={(event) => {
                setDropError(false);
                onChoose(event.target.files?.[0] ?? null);
                event.target.value = '';
              }}
            />
          </div>
          {dropError ? <Alert tone="warning">برای هر بخش فقط یک فایل انتخاب کنید.</Alert> : null}
          <div className="import-pack__availability">
            <V2Badge tone={pack.commitAvailability === 'ready' ? 'success' : 'warn'}>
              {pack.nativeWriteAvailable
                ? 'ثبت اصلی / پیش‌نویس'
                : pack.commitAvailability === 'ready'
                  ? 'آمادهٔ بررسی و ثبت'
                  : 'آمادهٔ بررسی'}
            </V2Badge>
            {pack.reason ? <small>{pack.reason}</small> : null}
          </div>
          {!pack.permitted ? (
            <Alert tone="warning">برای ورود اطلاعات این بخش دسترسی ندارید.</Alert>
          ) : null}
        </div>
      </V2Card>
    </section>
  );
}
